<template>
  <section class="mt-5 mb-5">
    <div class="container">
      <div class="row justify-content-center"> 
        <div class="col-md-8">
          <!-- Skeleton Loaders -->
          <div v-if="loading" class="skeleton-container">
            <div v-for="section in 3" :key="`section-${section}`" class="skeleton-section">
              <div class="skeleton-title"></div>
              <div class="skeleton-cards">
                <div 
                  v-for="card in 2" 
                  :key="`card-${section}-${card}`" 
                  class="skeleton-card"
                >
                  <div class="skeleton-image"></div>
                  <div class="skeleton-content">
                    <div class="skeleton-event-title"></div>
                    <div class="skeleton-text"></div>
                    <div class="skeleton-text short"></div>
                    <div class="skeleton-meta">
                      <div class="skeleton-badge"></div>
                      <div class="skeleton-badge"></div>
                    </div>
                    <div class="skeleton-actions">
                      <div class="skeleton-button"></div>
                      <div class="skeleton-button"></div>
                      <div class="skeleton-button"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Actual Content -->
          <template v-else>
            <InvitationList title="Active Invitations"   :items="active"   @rsvp-updated="loadInvitations" />
            <InvitationList title="Upcoming Invitations" :items="upcoming" @rsvp-updated="loadInvitations" />
            <InvitationList title="Past Invitations"     :items="past"     @rsvp-updated="loadInvitations" />
            <InvitationList title="Draft Invitations"    :items="draft"    @rsvp-updated="loadInvitations" />
          </template>
        </div>
      </div>

      <!-- Empty State -->
      <div v-if="!loading && !active.length && !upcoming.length && !past.length && !draft.length" class="row mt-4">
        <div class="col-12 text-center py-5">
          <img v-no-right-click 
            src="/assets/placeholders/no-invitations.png" 
            alt="No invitations" 
            style="max-width: 240px;" 
            class="mb-3"
            @error="handleImageError"
          >  
          <p class="text-muted mb-3">No invitations received yet</p>
          <p class="text-muted small">When someone sends you an invitation, it will appear here</p>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import api from '@/services/api' 
import InvitationList from '@/components/common/ReceivedInvitationList.vue' 
import { useSessionStore } from '@/stores/session'
import { useHead } from '@vueuse/head'

const active = ref([])
const upcoming = ref([])
const past = ref([])
const draft = ref([])
const loading = ref(true)
const session = useSessionStore()

let loadingTimeout = null

useHead({
  title: 'Received Invitations - Invi8',
  meta: [
    { name: 'description', content: 'View and manage invitations sent to you - active, upcoming, and past events.' },
    { name: 'keywords', content: 'received invitations, rsvp, event invites, guest management' },
  ],
})

// Handle broken images
const handleImageError = (event) => {
  event.target.style.display = 'none'
  const parent = event.target.parentElement
  if (parent) {
    const icon = document.createElement('i')
    icon.className = 'fas fa-envelope-open-text fa-3x mb-3 text-light'
    parent.insertBefore(icon, event.target.nextSibling)
  }
}

async function loadInvitations() {
  try {
    loading.value = true
    
    // Set minimum loading time for better UX
    loadingTimeout = setTimeout(() => {
      // Skeleton will show for at least 800ms
    }, 800)

    const formData = new FormData()
    formData.append('id', session.userId) 
    const response = await api.getReceivedInvitations(formData)
    
    // Handle different response formats
    if (response?.status || response?.data?.status === 'ok' || response?.data?.status === true) {
      const data = response.data || {}
      active.value   = data.list || []
      upcoming.value = data.list_upcoming || []
      past.value     = data.list_old || []
      draft.value    = data.list_draft || [] // Added draft if API provides it
    } else {
      // Handle API response without expected status
      active.value = []
      upcoming.value = []
      past.value = []
      draft.value = []
    }
  } catch (err) {
    console.error('Error loading received invitations:', err)
    // Set empty arrays on error
    active.value = []
    upcoming.value = []
    past.value = []
    draft.value = []
  } finally {
    loading.value = false
    if (loadingTimeout) {
      clearTimeout(loadingTimeout)
      loadingTimeout = null
    }
  }
}

onMounted(() => {
  loadInvitations()
})

// Cleanup timeout on component unmount
onMounted(() => {
  return () => {
    if (loadingTimeout) {
      clearTimeout(loadingTimeout)
    }
  }
})
</script>

<style scoped>
/* Skeleton Loader Styles */
.skeleton-container {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.skeleton-section {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.skeleton-title {
  height: 24px;
  width: 200px;
  border-radius: 6px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: skeleton-shimmer 1.5s infinite;
  margin-bottom: 0.5rem;
}

.skeleton-cards {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.skeleton-card {
  display: flex;
  gap: 1rem;
  padding: 1.25rem;
  border: 1px solid #f0f0f0;
  border-radius: 12px;
  background: #fff;
  animation: skeleton-pulse 1.5s ease-in-out infinite;
}

.skeleton-image {
  width: 80px;
  height: 80px;
  border-radius: 8px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: skeleton-shimmer 1.5s infinite;
  flex-shrink: 0;
}

.skeleton-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.skeleton-event-title {
  height: 20px;
  width: 70%;
  border-radius: 4px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: skeleton-shimmer 1.5s infinite;
}

.skeleton-text {
  height: 14px;
  width: 90%;
  border-radius: 4px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: skeleton-shimmer 1.5s infinite;
}

.skeleton-text.short {
  width: 60%;
}

.skeleton-meta {
  display: flex;
  gap: 0.5rem;
  margin-top: 0.25rem;
}

.skeleton-badge {
  height: 20px;
  width: 60px;
  border-radius: 10px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: skeleton-shimmer 1.5s infinite;
}

.skeleton-actions {
  display: flex;
  gap: 0.5rem;
  margin-top: 0.75rem;
  flex-wrap: wrap;
}

.skeleton-button {
  height: 32px;
  width: 70px;
  border-radius: 6px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: skeleton-shimmer 1.5s infinite;
}

@keyframes skeleton-shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

@keyframes skeleton-pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.85;
  }
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .skeleton-card {
    flex-direction: column;
    text-align: center;
  }
  
  .skeleton-image {
    width: 100%;
    height: 120px;
    margin: 0 auto;
  }
  
  .skeleton-content {
    align-items: center;
  }
  
  .skeleton-event-title,
  .skeleton-text {
    width: 100%;
  }
  
  .skeleton-text.short {
    width: 80%;
  }
  
  .skeleton-actions {
    justify-content: center;
  }
}

/* Layout styles */
.mt-5 {
  margin-top: 3rem;
}

.mb-5 {
  margin-bottom: 3rem;
}

.mt-4 {
  margin-top: 1.5rem;
}

.py-5 {
  padding-top: 3rem;
  padding-bottom: 3rem;
}

.mb-3 {
  margin-bottom: 1rem;
}

.text-muted {
  color: #6b7280 !important;
}

.small {
  font-size: 0.875rem;
}

.text-center {
  text-align: center;
}
</style>